import React, { useEffect, useState } from 'react';
import { Grid, Tooltip, IconButton } from '@material-ui/core';
import MUITableLoading from 'components/MUITableLoading/MUITableLoading';
import Layout from 'components/Layout/Layout';
import { DataRangeFilter } from 'components/DataRangeFilter/DataRangeFilter';
import { useLazyQuery } from '@apollo/client';
import { GET_CATEGORIES_BY_SITE } from 'queries/category';
import MUIDataTable, {
  MUIDataTableOptions,
  MUIDataTableColumn,
} from 'mui-datatables';
import {
  normalizeCategoriesDataFromQuery,
  showCategoryNameWithDepth,
} from 'utils/categories';
import PageTitle from 'components/PageTitle/PageTitle';
import { UserService } from 'services/User/UserService';
import PageError from 'components/PageError';

import { CategoryTranslate } from 'pages/categories/components/categoryTranslate.component';
import { ICategory } from 'pages/categories/interfaces/category.interface';
import { debounceSearchRender } from 'components/DebouncePlugin';
import { Refresh } from '@material-ui/icons';
import { labelCategoryStatuses } from 'pages/labels/labelCategoryStatuses';
import { TranslateCategoryDescription } from '../components/translateCategoryDescription.component';
import { CategoryStatuses } from '../enums/category-statuses.enum';
import { TryAgainTranslateCategories } from '../components/tryAgainTranslateCategories.component';

export default function Categories(): JSX.Element {
  const [filterSite, setFilterSite] = useState<string[] | undefined>(undefined);
  const [categories, setCategories] = useState<ICategory[]>([]);

  const sites = UserService.useSitesFromAccess();

  const [loadCategories, { loading, error, data }] = useLazyQuery(
    GET_CATEGORIES_BY_SITE,
    {
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    if (data) {
      setCategories(data.contentCategoriesBySite);
    }
  }, [data]);

  useEffect(() => {
    let mounted = true;

    if (mounted && filterSite?.length) {
      loadCategories({
        variables: {
          siteDomain: filterSite[0],
          reload: false,
        },
      });
    }

    return () => (mounted = false as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSite]);

  const refreshList = () => (
    <Tooltip title="Refresh categories">
      <IconButton
        aria-label="refresh"
        onClick={() => {
          loadCategories({
            variables: {
              siteDomain: filterSite?.length ? filterSite[0] : '',
              reload: true,
            },
          });
        }}
      >
        <Refresh />
      </IconButton>
    </Tooltip>
  );

  const permissions = {
    showSitesFilter: true,
  };

  const columns: MUIDataTableColumn[] = [
    {
      name: 'name',
      label: 'CATEGORY NAME',
      options: {
        customBodyRender: (_value, tableMeta) =>
          showCategoryNameWithDepth({
            depth: tableMeta.rowData[3],
            name: tableMeta.rowData[0],
          }),
        empty: true,
        filter: true,
        sort: false,
      },
    },
    {
      name: 'id',
      label: 'ID',
      options: {
        display: false,
      },
    },
    {
      name: 'children',
      label: 'children',
      options: {
        display: false,
      },
    },
    {
      name: 'depth',
      label: 'depth',
      options: {
        display: false,
      },
    },
    {
      name: 'status',
      label: 'STATUS',
      options: {
        sort: false,
        customBodyRender: (status: CategoryStatuses) => {
          return labelCategoryStatuses[status];
        },
      },
    },
    {
      name: 'description',
      label: 'DESCRIPTION',
      options: {
        // eslint-disable-next-line react/display-name
        customBodyRender: (value) => {
          return (
            <>
              <div
                style={{ maxWidth: '800px' }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: value }}
              />
            </>
          );
        },
      },
    },
  ];

  const categoriesNormalized = normalizeCategoriesDataFromQuery(categories);

  const options: MUIDataTableOptions = {
    // eslint-disable-next-line react/display-name
    customToolbarSelect: (selectedRows) => (
      <CategoryTranslate
        selectedRows={selectedRows}
        categories={categoriesNormalized}
        siteDomain={filterSite?.length ? filterSite[0] : ''}
        loadCategories={loadCategories}
      />
    ),
    customSearchRender: debounceSearchRender(700),
  };

  return (
    <Layout>
      <Grid container spacing={0}>
        <Grid item xs={8}>
          <PageTitle title="Categories" />
          {filterSite?.length && (
            <>
              <br />
              <Grid container spacing={0}>
                <Grid item xs={2}>
                  <div style={{ display: 'flex', columnGap: '12px' }}>
                    <TranslateCategoryDescription siteDomain={filterSite[0]} />
                  </div>
                </Grid>
                <Grid item xs={2}>
                  <div style={{ display: 'flex', columnGap: '12px' }}>
                    <TryAgainTranslateCategories siteDomain={filterSite[0]} />
                  </div>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
        <Grid item xs={4}>
          <DataRangeFilter
            filterSite={filterSite}
            setFilterSite={(setFilterSite as unknown) as undefined}
            sites={sites}
            permissions={(permissions as unknown) as undefined}
            customColumnSize={12}
          />
        </Grid>
      </Grid>

      {loading ? (
        <MUITableLoading
          withRefresh
          selectable="none"
          columns={['CATEGORY NAME', 'STATUS', 'DESCRIPTION']}
        />
      ) : error ? (
        <PageError />
      ) : (
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <MUIDataTable
              title={refreshList()}
              data={categoriesNormalized}
              columns={columns}
              options={options}
            />
          </Grid>
        </Grid>
      )}
    </Layout>
  );
}
